import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  CommentaireRequestDto,
  CommentaireResponseDto,
  CommentaireViolationDtoFormFieldEnum,
} from 'lib_api/lib/api/gen';

import { SubmitFunction } from 'components/BaseForm/types';
import { useApi } from 'hooks/ApiStoreContext';
import { backAlertMessage } from 'hooks/utils/backAlertMessage';
import { sortCommentaireListDtoByDateDesc } from 'utils/commentaireUtils';

export function useValidateCommentaireField(
  dossierId: string,
  commentaire?: CommentaireResponseDto,
) {
  const { CommentaireControllerApi: controller } = useApi();
  const validateMutation = useMutation({
    mutationFn: ({
      field,
      values,
    }: {
      field: CommentaireViolationDtoFormFieldEnum;
      values: CommentaireRequestDto;
    }) => {
      return (
        commentaire
          ? controller.validateFieldUpdateCommentaireUsingPOST(
              dossierId,
              field,
              {
                commentaireId: commentaire.id,
                ...values,
              },
            )
          : controller.validateAddCommentaireFieldUsingPOST(
              dossierId,
              field,
              values,
            )
      ).then(response => {
        const violations = response.commentaireViolationDtos ?? [];
        if (violations.length === 0) {
          return Promise.resolve();
        }
        return Promise.reject(violations[0].message);
      });
    },
  });

  return (
    field: CommentaireViolationDtoFormFieldEnum,
    values: CommentaireRequestDto,
  ): Promise<void> => validateMutation.mutateAsync({ field, values }); // No .then() here anymore
}

export function useSubmitCommentaire(
  dossierId: string,
  close: () => void,
  commentaire?: CommentaireResponseDto,
): SubmitFunction<CommentaireRequestDto> {
  const queryClient = useQueryClient();

  const { CommentaireControllerApi: controller } = useApi();
  const mutation = useMutation({
    mutationFn: (values: CommentaireRequestDto) =>
      commentaire
        ? controller.updateCommentaireUsingPOST(dossierId, {
            commentaireId: commentaire.id,
            ...values,
          })
        : controller.addCommentaireUsingPOST(dossierId, values),
    onSuccess: commentaires => {
      close();
      return queryClient.setQueryData(
        ['commentaires', dossierId],
        sortCommentaireListDtoByDateDesc(commentaires),
      );
    },
    onError: (error: Response) => {
      void queryClient.invalidateQueries({
        queryKey: ['commentaires', dossierId],
      });
      void backAlertMessage(error);
    },
  });

  const submit = useCallback(
    (values: CommentaireRequestDto) => {
      return mutation.mutate(values);
    },
    [mutation],
  );

  return {
    submit: submit,
    inProgress: mutation.isPending,
  };
}
